import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { AuthClientInstance, getAuthClient } from "@trumpf/ux.utils";
import { debounce } from "../../utils/debounce";
import {
    createHash,
    getCodeChallenge,
    getCodeVerifier,
    getQueryString,
    getRandomString,
    getRPIframeSrcdoc,
    getUrlOrigin
} from "./silent-auth-utils";

interface AuthPayload {
    client_id: string;
    code_challenge: string;
    code_challenge_method: "S256";
    prompt: "none" | "login";
    redirect_uri: string;
    response_type: "code";
    scope: string;
    state: string;
}

interface AuthTokenData {
    access_token: string;
    expires_in: number;
    id_token?: string;
    refresh_token?: string;
    token_type?: string;
}

const TRUMPF_ISSUERS_PROD = ["https://identity.trumpf.com"];
const TRUMPF_ISSUERS_STAGE = ["https://identity-s.trumpf.com", "https://identity-t.trumpf.com"];
const TRUMPF_CALLBACK_URI_PROD = "https://cdn.trumpf.com/static/auth-callback.html";
const TRUMPF_CALLBACK_URI_STAGE = "https://cdn-q.trumpf.com/static/auth-callback.html";

@Component({
    tag: "ux-u-silent-auth",
    styleUrl: "silent-auth.scss",
    shadow: false
})
export class SilentAuth {
    /* eslint-disable @typescript-eslint/naming-convention */
    private _authClient: AuthClientInstance;
    private _authClientId: string;
    private _authTokenData: AuthTokenData | null = null;
    // @ts-expect-error Check for typing
    private _authIframe: HTMLIFrameElement = null;
    private _authScopes: string[];
    private _authCallbackHandler: (event: MessageEvent) => void;
    private _codeVerifier: string;
    // @ts-expect-error Check for typing
    private _rpIframe: HTMLIFrameElement = null;
    private _rpIframeInterval: number;
    private _sessionState: string;
    private _checkSessionEnabled: boolean;
    private _startCheckSessionIntervalTimeout: number;
    private _uid: string;
    private _refreshTokenTimeout: number;
    private _authFailureTimeout: number;
    /* eslint-enable @typescript-eslint/naming-convention */

    private _log = (msg: string) => {
        /* eslint-disable no-console */
        if (this.debug && typeof console !== "undefined" && typeof console.log === "function") {
            console.log(`[AUTH]: ${msg}`);
        }
        /* eslint-enable no-console */
    };

    constructor() {
        this._checkSessionEnabled = /.trumpf.com$/.test(window.location.hostname);
    }

    /**
     * The DOM node
     */
    @Element() domElement!: HTMLUxUSilentAuthElement;

    /**
     * URL of the identity provider
     */
    @Prop() readonly issuer: string = "https://identity.trumpf.com";

    /**
     * Auth client ID (is hidden when the component mounts)
     */
    @Prop() readonly clientId: string;

    /**
     * Name of the client, used to get the scoped AuthClient instance
     */
    @Prop() readonly clientName: string;

    /**
     * Requested scopes
     */
    @Prop() readonly scope: string = "oicd";

    /**
     * Auth callback URL. Only needs to be provided for issuers other than https://identity*.trumpf.com
     */
    @Prop({ mutable: true }) redirectUri: string;

    /**
     * Time in seconds after which the authentication flow is considered as failed
     */
    @Prop() readonly failedTimeout: number = 10;

    /**
     * If set to true, infos are logged to the console
     */
    @Prop() readonly debug: boolean;

    /**
     * Automatically generated hash used to find related components in the DOM tree
     */
    @Prop({ reflect: true, mutable: true }) hash: string;

    /**
     * Automatically set flag that indicates whether this instance is commissioned to perform authentication
     */
    @Prop({ reflect: true, mutable: true }) active: boolean;

    @State() loggedIn: boolean;

    /**
     * Emitted when the component is loaded to inform the delegated instance about scopes that may have changed
     */
    @Event({
        eventName: "uxSilentAuthAdded",
        bubbles: true,
        cancelable: false
    })
    instanceAdded!: EventEmitter<{
        hash: string;
        scope: string;
    }>;

    /**
     * Emitted when the component is activated
     */
    @Event({
        eventName: "uxSilentAuthActivated",
        bubbles: true,
        cancelable: false
    })
    instanceActivated!: EventEmitter<{
        hash: string;
    }>;

    /**
     * Emitted after the authentication flow was completed.
     * At this point either a token was fetched successfully and broadcasted to the `AuthClient`,
     * or silent authentication failed for some reason.
     */
    @Event()
    uxAuth!: EventEmitter<{
        success: boolean;
        error: string | null;
    }>;

    /** If this is the active instance, silent code flow has to be performed if scopes have changed */
    @Listen("uxSilentAuthAdded", { target: "window" })
    onClientAdded(
        event: CustomEvent<{
            hash: string;
            scope: string;
        }>
    ) {
        if (event.detail.hash === this.hash && this.active && this.loggedIn) {
            if (Array.isArray(this._authScopes) && this.collectScopes().toString() !== this._authScopes.toString()) {
                this.stopCheckSessionInterval();
                // @ts-expect-error Check for typing
                this.loggedIn = undefined;
                this.initSilentAuthDebounced();
            }
        }
    }

    @Watch("active")
    handleActive() {
        if (this.active) {
            this.instanceActivated.emit({
                hash: this.hash
            });
            this.initSilentAuthDebounced();
        }
    }

    @Watch("loggedIn")
    handleLoggedIn() {
        if (this.loggedIn && this._checkSessionEnabled) {
            this._startCheckSessionIntervalTimeout = window.setTimeout(this.startCheckSessionInterval, 100);
        } else {
            this.stopCheckSessionInterval();
        }
    }

    componentWillLoad() {
        this.active = false;
        this._authClientId = this.clientId;
        this.domElement.removeAttribute("client-id");
        this._authClient =
            typeof this.clientName === "string" && this.clientName.length > 0 ? getAuthClient(this.clientName) : getAuthClient();
        this._uid = getRandomString(32);
        this.hash = createHash([this._authClientId, this.clientName || "global"].join("-")).replace("-", "");
        if (this.redirectUri === undefined) {
            if (TRUMPF_ISSUERS_PROD.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_PROD;
            } else if (TRUMPF_ISSUERS_STAGE.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_STAGE;
            }
        }

        this._authTokenData = this._authClient.getTokenData();
        if (this._authTokenData !== null) {
            this._log("Reading token data from authClient");
        }
    }

    componentDidLoad() {
        this.activateDelegateDebounced();
        this.instanceAdded.emit({
            hash: this.hash,
            scope: this.scope
        });
    }

    disconnectedCallback() {
        this.stopCheckSessionInterval();
        this.cancelRefreshTokenTimeout();
    }

    private activateDelegate = () => {
        this.findDelegatedInstance().active = true;
    };

    private activateDelegateDebounced = debounce(this.activateDelegate, 100);

    private initSilentAuth = async () => {
        this.cancelRefreshTokenTimeout();
        if (!this.active) {
            return;
        }
        this._log("Init silent auth");
        // @ts-expect-error Check for typing
        this.loggedIn = undefined;
        // @ts-expect-error Check for typing
        if (this._authTokenData !== null && this._authTokenData.refresh_token?.length > 0) {
            try {
                const res = await this.refreshToken();
                if (res?.access_token) {
                    return;
                }
            } catch {
                // Refreshing failed
            }
            this._log("Token refresh failed");
        }

        this._log("Request token via silent code flow");
        const payload = await this.initAuthPayload();
        this._authScopes = payload.scope.split(" ");
        this.initAuthCallback(this._authIframe);
        this._authIframe.src = `${this.issuer}/oauth2/authorize?${getQueryString(payload)}`;

        // Timeout if the auth iframe did not receive a message within 10 seconds (e.g. because redirection failed for some reason).
        window.clearTimeout(this._authFailureTimeout);
        this._authFailureTimeout = window.setTimeout(() => {
            if (this.loggedIn === undefined) {
                this.uxAuth.emit({ success: false, error: "timeout" });
            }
        }, this.failedTimeout * 1000);
    };

    private initSilentAuthDebounced = debounce(this.initSilentAuth, 100);

    private initAuthCallback = (iframe: HTMLIFrameElement) => {
        if (typeof this._authCallbackHandler === "function") {
            window.removeEventListener("message", this._authCallbackHandler, true);
        }
        const authMessageHandler = (event: MessageEvent) => {
            if (event.origin !== getUrlOrigin(this.redirectUri)) {
                return;
            }
            if (event.source !== iframe.contentWindow) {
                return;
            }
            window.removeEventListener("message", this._authCallbackHandler, true);
            const { code, error } = event.data;
            if (typeof code === "string") {
                this._log("Got one-time code");
                this._sessionState = event.data.session_state;
                this.requestToken(code);
                return;
            }
            if (typeof error === "string") {
                this._log(`Error: ${error}`);
                this._authClient.deleteAccessToken();
                this._authTokenData = null;
                this.loggedIn = false;

                this.uxAuth.emit({ success: false, error: error });
                return;
            }
        };
        this._authCallbackHandler = authMessageHandler.bind(this);
        window.addEventListener("message", this._authCallbackHandler, true);
    };

    private findDelegatedInstance = (): HTMLUxUSilentAuthElement => {
        return (
            Array.from(document.querySelectorAll<HTMLUxUSilentAuthElement>(`ux-u-silent-auth[hash="${this.hash}"]`)).reduce(
                // @ts-expect-error Check for typing
                (_previousValue, currentValue) => {
                    if (currentValue.active) {
                        return currentValue;
                    }
                }
            ) || document.querySelector<HTMLUxUSilentAuthElement>(`ux-u-silent-auth[hash="${this.hash}"]`)
        );
    };

    private collectScopes = (): string => {
        const collectedScopes: string[] = this.scope.split(" ");
        Array.from(document.querySelectorAll<HTMLUxUSilentAuthElement>(`ux-u-silent-auth[hash="${this.hash}"]`)).forEach((el) => {
            el.scope.split(" ").forEach((elScope) => {
                if (!collectedScopes.includes(elScope)) {
                    collectedScopes.push(elScope);
                }
            });
        });
        return collectedScopes.sort().join(" ");
    };

    private initAuthPayload = async (): Promise<AuthPayload> => {
        this._codeVerifier = getCodeVerifier();
        const codeChallenge = await getCodeChallenge(this._codeVerifier);
        return {
            client_id: this._authClientId,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
            prompt: "none",
            redirect_uri: this.redirectUri,
            response_type: "code",
            scope: this.collectScopes(),
            // @ts-expect-error Check for typing
            state: window.top.location.href
        };
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private requestToken = async (code: string): Promise<any> => {
        this._log("Request token with authorization_code grant");
        return this.sendTokenRequest({
            grant_type: "authorization_code",
            response_type: "token",
            redirect_uri: this.redirectUri,
            client_id: this._authClientId,
            scope: this.scope,
            code_verifier: this._codeVerifier,
            code
        });
    };

    private refreshToken = () => {
        this._log("Request token with refresh_token grant");
        return this.sendTokenRequest({
            grant_type: "refresh_token",
            client_id: this._authClientId,
            // @ts-expect-error Check for typing
            refresh_token: this._authTokenData.refresh_token
        });
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private sendTokenRequest = (data: any) => {
        return fetch(`${this.issuer}/oauth2/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            redirect: "follow",
            body: getQueryString(data)
        })
            .then((response) => response.json())
            .then((data) => {
                this._log("Got access token");
                this.handleTokenResponse(data);
                return data;
            });
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private handleTokenResponse = (data: any) => {
        if (typeof data === "object" && typeof data.access_token === "string") {
            this._authTokenData = data as AuthTokenData;
            if (this._authTokenData.refresh_token && this._authTokenData.expires_in > 0) {
                const delay = Math.round(0.75 * this._authTokenData.expires_in);
                this._refreshTokenTimeout = window.setTimeout(this.initSilentAuth, delay * 1000);
            }
            this.loggedIn = true;
            // FIXME: We need the timeout as the access_token is not instantly valid in all scenarios
            window.setTimeout(() => {
                // @ts-expect-error Check for typing
                this._authClient.setTokenData(this._authTokenData);
                this.uxAuth.emit({ success: true, error: null });
            }, 2500);
        } else {
            this._authTokenData = null;
            this.loggedIn = false;
        }
    };

    private cancelRefreshTokenTimeout = () => {
        window.clearTimeout(this._refreshTokenTimeout);
    };

    private startCheckSessionInterval = () => {
        this.stopCheckSessionInterval();
        if (!this.active || !this.loggedIn) {
            return;
        }
        if (!(typeof this._rpIframe?.contentWindow !== "undefined")) {
            return;
        }
        this._log("Starting check-session interval");
        // @ts-expect-error Check for typing
        this._rpIframe.contentWindow.addEventListener("message", this.handleCheckSessionMessage, true);
        this._rpIframeInterval = window.setInterval(() => {
            try {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                (this._rpIframe.contentWindow as any).poll();
            } catch (e) {
                /* eslint-disable-next-line no-console */
                console.error(e);
                this.stopCheckSessionInterval();
            }
        }, 2000);
    };

    private stopCheckSessionInterval = () => {
        window.clearTimeout(this._startCheckSessionIntervalTimeout);
        window.clearInterval(this._rpIframeInterval);
    };

    private handleCheckSessionMessage = (event: MessageEvent) => {
        switch (event.data) {
            case "unchanged":
                break;
            case "changed":
                this._log("Session changed");
                this.stopCheckSessionInterval();
                // @ts-expect-error Check for typing
                this.loggedIn = undefined;
                this.initSilentAuth();
                break;
            case "error":
                this.stopCheckSessionInterval();
                // @ts-expect-error Check for typing
                this.loggedIn = undefined;
                this._authClient.deleteAccessToken();
                break;
            default:
                break;
        }
    };

    render() {
        if (!this.active) {
            return <Host class="ux-u-silent-auth" />;
        }
        let opIframeSrc = "";
        let rpIframeSrcDoc = "";
        if (this._checkSessionEnabled && this.loggedIn) {
            opIframeSrc = `${this.issuer}/oidc/checksession?${getQueryString({
                client_id: this._authClientId,
                redirect_uri: this.redirectUri
            })}`;
            rpIframeSrcDoc = getRPIframeSrcdoc(
                {
                    issuer: this.issuer,
                    clientId: this._authClientId,
                    sessionState: this._sessionState
                },
                `OP-${this._uid}`
            );
        }
        return (
            <Host class="ux-u-silent-auth">
                {/* @ts-expect-error Check for typing */}
                <iframe ref={(el) => (this._authIframe = el)} />
                {this._checkSessionEnabled && this.loggedIn && (
                    <div>
                        {/* @ts-expect-error Check for typing */}
                        <iframe id={`RP-${this._uid}`} ref={(el) => (this._rpIframe = el)} srcDoc={rpIframeSrcDoc} />
                        <iframe id={`OP-${this._uid}`} src={opIframeSrc} />
                    </div>
                )}
            </Host>
        );
    }
}
